import { CollapsibleFilters } from '../common/CollapsibleFilters';
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../lib/api";
import type { TaskTemplate, TaskType, TaskPriority } from "../../types/tasks";
import { useDebounce } from "../../hooks/useDebounce";

export function TaskTemplates() {
  const [search, setSearch] = useState("");
  const [archived, setArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<Partial<TaskTemplate> | null>(null);
  const query = useDebounce(search, 250);
  const cache = useQueryClient();
  const list = useQuery({
    queryKey: ["task-templates", query, archived, page],
    queryFn: async () =>
      (
        await apiClient.get<{
          results: TaskTemplate[];
          count: number;
          next: string | null;
        }>(
          `/api/task-templates/?search=${encodeURIComponent(query)}&is_archived=${archived}&page=${page}`,
        )
      ).data,
  });
  const save = useMutation({
    mutationFn: async (row: Partial<TaskTemplate>) =>
      row.id
        ? apiClient.patch(`/api/task-templates/${row.id}/`, row)
        : apiClient.post("/api/task-templates/", row),
    onSuccess: () => {
      cache.invalidateQueries({ queryKey: ["task-templates"] });
      cache.invalidateQueries({ queryKey: ["task-picker"] });
      setDraft(null);
    },
  });
  return (
    <Stack spacing={2}>
      <Button
          onClick={() => {
            save.reset();
            setDraft({
              name: "",
              task_type: "other",
              priority: "medium",
              description: "",
              instructions: "",
            });
          }}
        >
          New template
        </Button>
<CollapsibleFilters><Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Input
          sx={{ flex: 1 }}
          placeholder="Search templates"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

      </Box></CollapsibleFilters>
      <Checkbox
        label="Show archived"
        checked={archived}
        onChange={(e) => {
          setArchived(e.target.checked);
          setPage(1);
        }}
      />
      {(list.isError || save.isError) && (
        <Alert color="danger">
          Unable to save or load templates.{" "}
          {String((save.error as any)?.message || "")}
          <Button onClick={() => list.refetch()}>Retry</Button>
        </Alert>
      )}
      {list.isLoading && <Typography>Loading templates…</Typography>}
      {list.data?.results.length === 0 && (
        <Typography>
          No templates match. Create a reusable recipe for your team.
        </Typography>
      )}
      {list.data?.results.map((row) => (
        <Card key={row.id}>
          <Typography level="title-lg">{row.name}</Typography>
          <Typography>{row.description || "No description"}</Typography>
          <Typography level="body-sm">
            {row.task_type.replaceAll("_", " ")} · {row.priority} priority ·{" "}
            {row.estimated_hours || "—"} hours
          </Typography>
          <Typography sx={{ whiteSpace: "pre-wrap" }}>
            {row.instructions}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="sm"
              onClick={() => {
                save.reset();
                setDraft({ ...row });
              }}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="outlined"
              onClick={() => {
                save.reset();
                const { id, ...copy } = row;
                setDraft({
                  ...copy,
                  name: `${row.name} (copy)`,
                  is_archived: false,
                });
              }}
            >
              Duplicate
            </Button>
            <Button
              size="sm"
              variant="plain"
              disabled={save.isPending}
              onClick={() =>
                save.mutate({ id: row.id, is_archived: !row.is_archived })
              }
            >
              {row.is_archived ? "Restore" : "Archive"}
            </Button>
          </Box>
        </Card>
      ))}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Button
          disabled={page === 1 || list.isLoading}
          onClick={() => setPage(page - 1)}
        >
          Previous
        </Button>
        <Typography>
          Page {page} · {list.data?.count ?? "—"} templates
        </Typography>
        <Button
          disabled={!list.data?.next || list.isLoading}
          onClick={() => setPage(page + 1)}
        >
          Next
        </Button>
      </Box>
      <Modal open={!!draft} onClose={() => setDraft(null)}>
        <ModalDialog sx={{ width: "90vw", maxWidth: 620, overflow: "auto" }}>
          <Typography level="h4">
            {draft?.id ? "Edit" : "New"} template
          </Typography>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (draft) save.mutate(draft);
            }}
          >
            <Stack spacing={2}>
              {(["name", "description", "instructions"] as const).map(
                (field) => (
                  <FormControl key={field}>
                    <FormLabel>
                      {field.charAt(0).toUpperCase() + field.slice(1)}
                    </FormLabel>
                    <Textarea
                      required={field === "name"}
                      value={draft?.[field] || ""}
                      onChange={(e) =>
                        setDraft({ ...draft, [field]: e.target.value })
                      }
                      minRows={field === "name" ? 1 : 3}
                    />
                  </FormControl>
                ),
              )}
              <FormControl>
                <FormLabel>Type</FormLabel>
                <Select
                  value={draft?.task_type || "other"}
                  onChange={(_, value) =>
                    setDraft({ ...draft, task_type: value as TaskType })
                  }
                >
                  {[
                    "phone_call",
                    "meeting",
                    "site_visit",
                    "inspection",
                    "installation",
                    "repair",
                    "maintenance",
                    "documentation",
                    "follow_up",
                    "material_order",
                    "scheduling",
                    "cleanup",
                    "other",
                  ].map((type) => (
                    <Option key={type} value={type}>
                      {type.replaceAll("_", " ")}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Priority</FormLabel>
                <Select
                  value={draft?.priority || "medium"}
                  onChange={(_, value) =>
                    setDraft({ ...draft, priority: value as TaskPriority })
                  }
                >
                  {["low", "medium", "high", "critical"].map((p) => (
                    <Option key={p} value={p}>
                      {p}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Estimated hours</FormLabel>
                <Input
                  type="number"
                  slotProps={{ input: { min: 0, step: 0.25 } }}
                  value={draft?.estimated_hours ?? ""}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      estimated_hours: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                />
              </FormControl>
              {save.isError && (
                <Alert color="danger">
                  {String(
                    (save.error as any)?.message || "Unable to save template",
                  )}
                </Alert>
              )}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  type="submit"
                  loading={save.isPending}
                  disabled={!draft?.name?.trim()}
                >
                  Save template
                </Button>
                <Button variant="plain" onClick={() => setDraft(null)}>
                  Cancel
                </Button>
              </Box>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>
    </Stack>
  );
}
